import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { appointmentAPI, nailTechAPI, serviceAPI, clientAPI, depositAPI } from '../services/api';
import { formatUSPhoneInput, toTwilioUSPhone } from '../utils/phone';
import { useDebounce } from '../utils/hooks';
import './BookingForm.css';

export default function BookingForm({ onAppointmentCreated, prefillData = {} }) {
  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
    nailTechId: '',
    serviceId: '',
    appointmentDate: prefillData.appointmentDate || '',
    startTime: prefillData.startTime || '',
    notes: '',
  });

  const [nailTechs, setNailTechs] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSearchField, setActiveSearchField] = useState('');
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const debouncedName = useDebounce(formData.clientName, 300);
  const debouncedPhone = useDebounce(formData.clientPhone, 300);

  // Debounced client search — avoid filtering on every keystroke
  useEffect(() => {
    if (activeSearchField) {
      const val = activeSearchField === 'clientName' ? debouncedName : debouncedPhone;
      const nextSuggestions = getMatchingClients(val);
      setSuggestions(nextSuggestions);
      setActiveSuggestionIndex(nextSuggestions.length ? 0 : -1);
    }
  }, [debouncedName, debouncedPhone, activeSearchField]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [techsRes, servicesRes] = await Promise.all([
          nailTechAPI.getAll(),
          serviceAPI.getAll(),
        ]);
        setNailTechs(techsRes.data);
        setServices(servicesRes.data);

        try {
          const clientsRes = await clientAPI.getAll();
          setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
        } catch (clientErr) {
          // Non-fatal: booking still works even if customer suggestions fail to load.
          console.error('Failed to load clients for suggestions:', clientErr);
        }
      } catch (err) {
        setError('Failed to load data');
      }
    };

    fetchData();
    
    // Update form when prefill data changes
    if (prefillData.appointmentDate || prefillData.startTime) {
      setFormData(prev => ({
        ...prev,
        appointmentDate: prefillData.appointmentDate || prev.appointmentDate,
        startTime: prefillData.startTime || prev.startTime,
      }));
    }
  }, [prefillData]);

  const getMatchingClients = (rawInput) => {
    const query = String(rawInput || '').trim().toLowerCase();
    const phoneQueryDigits = String(rawInput || '').replace(/\D/g, '');

    if (!query && !phoneQueryDigits) return [];

    return clients
      .filter((client) => {
        const clientName = String(client?.name || '').toLowerCase();
        const clientPhone = String(client?.phone || '');
        const normalizedPhone = String(client?.normalizedPhone || clientPhone);
        const phoneDigits = normalizedPhone.replace(/\D/g, '');

        const nameMatch = query && clientName.includes(query);
        const phoneMatch = phoneQueryDigits && phoneDigits.includes(phoneQueryDigits);

        return nameMatch || phoneMatch;
      })
      .slice(0, 8);
  };

  const updateSuggestions = (fieldName, nextValue) => {
    const nextSuggestions = getMatchingClients(nextValue);
    setActiveSearchField(fieldName);
    setSuggestions(nextSuggestions);
    setActiveSuggestionIndex(nextSuggestions.length ? 0 : -1);
  };

  const selectClientSuggestion = (client) => {
    const displayPhone = formatUSPhoneInput(client?.phone || client?.normalizedPhone || '');

    setFormData((prev) => ({
      ...prev,
      clientName: client?.name || '',
      clientPhone: displayPhone,
    }));

    setSuggestions([]);
    setActiveSearchField('');
    setActiveSuggestionIndex(-1);
  };

  const handleSuggestionKeyDown = (e, fieldName) => {
    if (!suggestions.length || activeSearchField !== fieldName) {
      if (e.key === 'Escape') {
        setSuggestions([]);
        setActiveSearchField('');
        setActiveSuggestionIndex(-1);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => {
        if (prev < 0) return 0;
        return (prev + 1) % suggestions.length;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => {
        if (prev < 0) return suggestions.length - 1;
        return (prev - 1 + suggestions.length) % suggestions.length;
      });
      return;
    }

    if (e.key === 'Enter') {
      if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
        e.preventDefault();
        selectClientSuggestion(suggestions[activeSuggestionIndex]);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setSuggestions([]);
      setActiveSearchField('');
      setActiveSuggestionIndex(-1);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'clientPhone') {
      formattedValue = formatUSPhoneInput(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    if (name === 'clientName' || name === 'clientPhone') {
      updateSuggestions(name, formattedValue);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const normalizedPhone = toTwilioUSPhone(formData.clientPhone);

      // ── Duplicate customer check (phone-only) ──────────────────────────
      if (normalizedPhone) {
        try {
          const existing = await clientAPI.findByPhone(normalizedPhone);
          if (existing.data) {
            const existingName = existing.data.name || existing.data.clientName || 'Unknown';
            const proceed = window.confirm(
              `Phone number ${formData.clientPhone} is already registered to "${existingName}".\n\nContinue booking anyway?`
            );
            if (!proceed) {
              setLoading(false);
              return;
            }
          }
        } catch (dupErr) {
          // Non-fatal — proceed if lookup fails
          console.error('Duplicate check failed:', dupErr);
        }
      }

      const response = await appointmentAPI.create({
        ...formData,
        clientPhone: normalizedPhone,
        deposit: depositAPI.computeDeposit(
          services.find((s) => s._id === formData.serviceId) || null
        ),
      });

      if (formData.clientName.trim() && normalizedPhone) {
        try {
          await clientAPI.ensureByNamePhone(formData.clientName.trim(), normalizedPhone);
        } catch (clientError) {
          // Keep booking successful even if customer sync fails.
          console.error('Failed to sync customer list:', clientError);
        }
      }

      const deposit = depositAPI.computeDeposit(
        services.find((s) => s._id === formData.serviceId) || null
      );
      if (deposit) {
        toast(`Appointment booked! $${deposit.amount} deposit due via Zelle/CashApp.`, {
          icon: '💳',
        });
      } else {
        toast.success('Appointment booked successfully!');
      }
      setFormData({
        clientName: '',
        clientPhone: '',
        nailTechId: '',
        serviceId: '',
        appointmentDate: '',
        startTime: '',
        notes: '',
      });
      setSuggestions([]);
      setActiveSearchField('');
      setActiveSuggestionIndex(-1);
      if (onAppointmentCreated) {
        onAppointmentCreated(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="booking-form">
      <h2>Make Appointment</h2>
      {error && <p className="error">{error}</p>}

      <div className="form-group">
        <label htmlFor="clientName">Client Name *</label>
        <input
          type="text"
          name="clientName"
          id="clientName"
          value={formData.clientName}
          onChange={handleChange}
          onFocus={() => updateSuggestions('clientName', formData.clientName)}
          onKeyDown={(e) => handleSuggestionKeyDown(e, 'clientName')}
          onBlur={() => {
            setTimeout(() => {
              setSuggestions([]);
              setActiveSearchField('');
              setActiveSuggestionIndex(-1);
            }, 150);
          }}
          required
        />
        {activeSearchField === 'clientName' && suggestions.length > 0 && (
          <div className="client-suggestions" role="listbox" aria-label="Client suggestions">
            {suggestions.map((client, index) => (
              <button
                key={client._id}
                type="button"
                className={`client-suggestion-item ${index === activeSuggestionIndex ? 'is-active' : ''}`}
                onMouseDown={() => selectClientSuggestion(client)}
              >
                <span className="client-suggestion-name">{client.name || 'Unknown'}</span>
                <span className="client-suggestion-phone">
                  {formatUSPhoneInput(client.phone || client.normalizedPhone || '') || client.phone || ''}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="clientPhone">Phone</label>
        <input
          type="tel"
          name="clientPhone"
          id="clientPhone"
          value={formData.clientPhone}
          onChange={handleChange}
          onFocus={() => updateSuggestions('clientPhone', formData.clientPhone)}
          onKeyDown={(e) => handleSuggestionKeyDown(e, 'clientPhone')}
          onBlur={() => {
            setTimeout(() => {
              setSuggestions([]);
              setActiveSearchField('');
              setActiveSuggestionIndex(-1);
            }, 150);
          }}
          placeholder="+1-702-555-1234"
          maxLength="15"
        />
        {activeSearchField === 'clientPhone' && suggestions.length > 0 && (
          <div className="client-suggestions" role="listbox" aria-label="Client suggestions">
            {suggestions.map((client, index) => (
              <button
                key={client._id}
                type="button"
                className={`client-suggestion-item ${index === activeSuggestionIndex ? 'is-active' : ''}`}
                onMouseDown={() => selectClientSuggestion(client)}
              >
                <span className="client-suggestion-name">{client.name || 'Unknown'}</span>
                <span className="client-suggestion-phone">
                  {formatUSPhoneInput(client.phone || client.normalizedPhone || '') || client.phone || ''}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="nailTechId">Nail Technician</label>
        <select
          name="nailTechId"
          id="nailTechId"
          value={formData.nailTechId}
          onChange={handleChange}
        >
          <option value="">Select a technician</option>
          {nailTechs.map((tech) => (
            <option key={tech._id} value={tech._id}>
              {tech.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="serviceId">Service</label>
        <select
          name="serviceId"
          id="serviceId"
          value={formData.serviceId}
          onChange={handleChange}
        >
          <option value="">Select a service</option>
          {services.map((service) => (
            <option key={service._id} value={service._id}>
              {service.name} (${service.price}){service.duration ? ` • ${service.duration}min` : ''}
            </option>
          ))}
        </select>
        {(() => {
          const info = depositAPI.computeDeposit(
            services.find((s) => s._id === formData.serviceId) || null
          );
          if (!info) return null;
          return (
            <p className="deposit-note" role="note">
              💳 Deposit required: ${info.amount}
              {info.type === 'percent' ? ` (${info.value}% of service price)` : ''} — payable via
              Zelle or CashApp.
            </p>
          );
        })()}
      </div>

      <div className="form-group">
        <label htmlFor="appointmentDate">Date *</label>
        <input
          type="date"
          name="appointmentDate"
          id="appointmentDate"
          value={formData.appointmentDate}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="startTime">Start Time *</label>
        <input
          type="time"
          name="startTime"
          id="startTime"
          value={formData.startTime}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="notes">Notes</label>
        <textarea
          name="notes"
          id="notes"
          value={formData.notes}
          onChange={handleChange}
          rows="3"
        />
      </div>

      <button type="submit" disabled={loading}>
        {loading ? 'Saving...' : 'Make Appointment'}
      </button>
    </form>
  );
}
